/*
  pdp-reviews-live.js
  WHAT: Replaces the curated review cards with the most recent reviews pulled
        live from Yotpo, capped at 25.
  WHY:  Owner 2026-08-21: "all the pages with reviews - if they do not have 20
        then list them all and they update dynamically based on yotpo content"
        and "this means the last 20 will show".

        Hand-curating 20 quotes per product into a metafield does not scale and
        goes stale the moment a new review lands. This reads Yotpo directly, so
        every PDP shows its 20 newest reviews and stays current with no
        maintenance. Products with fewer than 20 simply show all they have.
  WHEN: Added 2026-08-21.

  PROGRESSIVE ENHANCEMENT — deliberate:
    • Liquid renders the curated `custom.review_highlights` cards server-side.
      That is the first paint, works with JS off, and is what crawlers index.
    • This script then fetches Yotpo and swaps the cards in.
    • Any failure — offline, CORS change, rate limit, empty response — leaves the
      server-rendered cards untouched. The section can never end up empty
      because of this file.

  FEASIBILITY (verified 2026-08-21): the Yotpo widget API returns
  `Access-Control-Allow-Origin: https://blueant.com.au`, so a browser fetch from
  the storefront is permitted. The app key is a PUBLIC widget key — the same one
  Yotpo's own on-site widget exposes — so there is no secret in the theme.
*/
(function () {
  'use strict';

  /* Owner 2026-10-08: "we can even show 25, if people want to read they can." Was 20 from
     the 2026-08-21 instruction; the rail scrolls horizontally, so a longer list costs nothing
     above the fold and the extra reviews are there for anyone who wants them. */
  var MAX = 25;
  /* Owner 2026-10-08: "make sure that all reviews are being shown correctly on the draft and
     match the live in terms of numbers."

     This was 28 - "one-word reviews make poor cards". But the live site shows every approved
     review, so the threshold made the draft under-report: Pump Air Lite 3 reads 3 on live and
     was showing 2 here, because a genuine 5-star review reads "Its really good" (15 chars).
     Parity with live wins over card tidiness, and a short honest review is still a real one.

     Kept at 2 rather than 0 so a blank or single-character body is still skipped. Everything
     Yotpo returns is already approved - the API only serves published reviews - so this cannot
     surface anything unmoderated. */
  var MIN_LEN = 2;
  // Strip emoji: design system bans them in product copy.
  var EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}]/gu;

  function clean(s) {
    return String(s || '').replace(/\s+/g, ' ').replace(EMOJI, '').trim();
  }

  // Yotpo occasionally returns mojibake for smart quotes. A card showing "don�t"
  // is worse than showing one fewer review, so drop those outright.
  /* Owner 2026-10-08 asked that every PDP show 20 reviews where possible. Measured: Pump Air
     Nano had 23 reviews in Yotpo and showed 16, Pump Air Micro had 19 and showed 12.

     The cause was this check. It rejected any review containing U+FFFD outright - and in
     EVERY case the offender was a single mangled apostrophe inside a contraction:
     "They�re super lightweight", "they don�t have much". Thirteen complete, well-written
     reviews were being discarded over one character of bad encoding.

     So the character is now REPAIRED rather than used as grounds for rejection: between two
     letters it is what it plainly was, an apostrophe; anywhere else it is dropped. This is a
     transcoding fix, not an edit to the customer's words, so it keeps design rule 10's
     "real reviews unedited" intact.

     isBroken is kept as a last line of defence for text that is still corrupt after repair -
     several bad characters, or ones not sitting inside a word. */
  function repair(s) {
    return s
      .replace(/([A-Za-z])�([A-Za-z])/g, '$1’$2')
      .replace(/�/g, '');
  }

  function isBroken(s) {
    return s.indexOf('�') !== -1;
  }

  function stars(n) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += i <= n
        ? '<span class="ba-pdp-reviews__card-star is-filled">★</span>'
        : '<span class="ba-pdp-reviews__card-star">☆</span>';
    }
    return out;
  }

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Build a card with the SAME structure Liquid emits, so all existing CSS and
  // the horizontal scroller keep working with no changes.
  function card(r, i) {
    var el = document.createElement('article');
    el.className = 'ba-pdp-reviews__card';
    el.setAttribute('data-slot', 'pdp.reviews.card.' + i);
    el.setAttribute('data-review-source', 'yotpo');
    var who = r.name ? '<span class="ba-pdp-reviews__card-who">' + esc(r.name) + '</span>' : '';
    var head = r.title ? '<div class="ba-pdp-reviews__card-h">' + esc(r.title) + '</div>' : '';
    el.innerHTML =
      '<div class="ba-pdp-reviews__card-stars">' + stars(r.score) + '</div>' +
      head +
      '<p class="ba-pdp-reviews__card-quote">' + esc(r.body) + '</p>' +
      '<div class="ba-pdp-reviews__card-meta">' + who +
        '<span><span class="ba-pdp-reviews__card-verified">✓ Verified buyer</span>' +
        (r.when ? ' · ' + esc(r.when) : '') + '</span>' +
      '</div>';
    return el;
  }

  function relative(iso) {
    if (!iso) return '';
    var then = new Date(iso).getTime();
    if (!then) return '';
    var days = Math.floor((Date.now() - then) / 86400000);
    if (days <= 1) return 'today';
    if (days < 7) return days + ' days ago';
    if (days < 60) return Math.floor(days / 7) + ' weeks ago';
    if (days < 730) return Math.floor(days / 30) + ' months ago';
    return Math.floor(days / 365) + ' years ago';
  }

  function apply(section, reviews) {
    var rail = section.querySelector('.ba-pdp-reviews__cards');
    if (!rail || !reviews.length) return;

    var frag = document.createDocumentFragment();
    reviews.forEach(function (r, i) { frag.appendChild(card(r, i)); });

    rail.innerHTML = '';
    rail.appendChild(frag);
    rail.scrollLeft = 0;

    // The scroller measures the first child's width, and the progress bar tracks
    // scrollWidth — both are stale after a wholesale DOM swap. Nudge them.
    window.dispatchEvent(new Event('resize'));
    section.setAttribute('data-reviews-live', String(reviews.length));
  }

  function load(section) {
    var key = section.getAttribute('data-yotpo-key');
    var pid = section.getAttribute('data-yotpo-product');
    if (!key || !pid) return;

    // Ask for newest-first so the cap really is "the last 20".
    var url = 'https://api-cdn.yotpo.com/v1/widget/' + encodeURIComponent(key) +
              '/products/' + encodeURIComponent(pid) +
              '/reviews.json?per_page=40&page=1&sort=date&direction=desc';

    fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var list = data && data.response && data.response.reviews;

        /* Owner 2026-10-08: "lite3 still says 2 reviews instead of 3."
           Correct, and the cards were already right - the NUMBER was not. The label is
           rendered from `product.metafields.reviews.rating_count`, which Shopify syncs from
           Yotpo on its own schedule and which read 2 while Yotpo itself held 3. The cards come
           from the live fetch, so the two disagreed on the same page.
           The count and the average now come from the same live response as the cards, so they
           cannot drift apart and they update as reviews come in - no metafield sync to wait on.
           `total_review` is used rather than the rendered card count: with the 25 cap a product
           can legitimately show 25 cards out of 183 reviews, and the label should say 183. */
        var bl = data && data.response && data.response.bottomline;
        if (bl) {
          /* Document-wide, not section-scoped: the same numbers are rendered in the BUYBOX as
             well (`[data-yotpo-*]`), from the same stale metafield. Updating only the reviews
             section left the page saying 3 in one place and 2 in another - which is what the
             owner photographed. */
          var counts = document.querySelectorAll('[data-reviews-count], [data-yotpo-count]');
          var ratings = document.querySelectorAll('[data-reviews-rating], [data-yotpo-rating]');
          if (bl.total_review != null) {
            Array.prototype.forEach.call(counts, function (el) {
              el.textContent = String(bl.total_review);
            });
          }
          if (bl.average_score != null) {
            var avg = (Math.round(Number(bl.average_score) * 10) / 10).toFixed(1);
            Array.prototype.forEach.call(ratings, function (el) { el.textContent = avg; });
          }
        }

        if (!list || !list.length) return;
        var out = [];
        var seen = {};
        for (var i = 0; i < list.length && out.length < MAX; i++) {
          var v = list[i];
          var body = repair(clean(v.content));
          var name = repair(clean(v.user && v.user.display_name));
          if (body.length < MIN_LEN || !name) continue;
          if (isBroken(body) || isBroken(name)) continue;
          var k = body.slice(0, 40).toLowerCase();
          if (seen[k]) continue;              // Yotpo can return near-duplicates
          seen[k] = 1;
          out.push({
            score: Number(v.score) || 5,
            // Repaired too - the body and name were, and a bad character in the TITLE
            // still reached the page (seen on Pump Air Micro).
            title: repair(clean(v.title)),
            body: body,
            name: name,
            when: relative(v.created_at)
          });
        }
        apply(section, out);
      })
      .catch(function () { /* keep the server-rendered cards */ });
  }

  function init(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-section-type="pdp-reviews"][data-yotpo-product]');
    Array.prototype.forEach.call(sections, load);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
